// The sheet's controls above the grid (Leon, 2026-10-23): saved views, search
// (matches light up in the cells), group by, which columns show, export — and
// the active filters as chips, each one click to edit or remove.

import { Button } from "@bednbody/ui/components/button";
import { Checkbox } from "@bednbody/ui/components/checkbox";
import { Input } from "@bednbody/ui/components/input";
import { Label } from "@bednbody/ui/components/label";
import { NativeSelect, NativeSelectOption } from "@bednbody/ui/components/native-select";
import { Popover, PopoverContent, PopoverTrigger } from "@bednbody/ui/components/popover";
import {
  BookmarkSimpleIcon,
  ColumnsIcon,
  DownloadSimpleIcon,
  MagnifyingGlassIcon,
  XIcon,
} from "@phosphor-icons/react";
import { type ReactNode, useId, useState } from "react";
import type { FilterChip, SavedView } from "./sheet-view";

export interface SheetToolbarProps {
  saved: readonly SavedView[];
  activeView: string | null;
  onPickView: (name: string | null) => void;
  onSaveView: (name: string) => void;
  onDeleteView: (name: string) => void;
  search: string;
  onSearch: (value: string) => void;
  groupOptions: readonly { key: string; label: string }[];
  groupBy: string | null;
  onGroupBy: (key: string | null) => void;
  columns: readonly { key: string; label: string; hidden: boolean }[];
  onToggleColumn: (key: string) => void;
  shownRows: number;
  totalRows: number;
  onExportAll: () => void;
  chips: readonly FilterChip[];
  onEditChip: (key: string) => void;
  onRemoveChip: (key: string) => void;
  onClearFilters: () => void;
  /** The page's own controls, such as a date range. */
  extra?: ReactNode;
  /** What a saved view would keep, in words: the active filters and grouping. */
  saveSummary: string;
}

const UNSAVED = "__current";

export function SheetToolbar(props: SheetToolbarProps) {
  const id = useId();
  const [naming, setNaming] = useState(false);
  const [name, setName] = useState("");
  const hiddenColumns = props.columns.filter((column) => column.hidden).length;
  const hiddenRows = props.totalRows - props.shownRows;

  return (
    <div className="flex min-w-0 flex-col gap-3">
      <div className="flex min-w-0 flex-wrap items-center gap-2">
        <div className="relative w-full min-w-0 sm:w-80">
          <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            aria-label="Search this log"
            className="w-full pl-9"
            onChange={(event) => props.onSearch(event.target.value)}
            placeholder="Search refs, IDs, services…"
            type="search"
            value={props.search}
          />
        </div>
        {props.extra}
        <Label className="sr-only" htmlFor={`${id}-group`}>
          Group by
        </Label>
        <NativeSelect
          id={`${id}-group`}
          onChange={(event) => props.onGroupBy(event.target.value || null)}
          value={props.groupBy ?? ""}
        >
          <NativeSelectOption value="">No grouping</NativeSelectOption>
          {props.groupOptions.map((option) => (
            <NativeSelectOption key={option.key} value={option.key}>
              Group by {option.label}
            </NativeSelectOption>
          ))}
        </NativeSelect>
        <span className="flex-1" />
        <Label className="sr-only" htmlFor={`${id}-view`}>
          Saved view
        </Label>
        <NativeSelect
          id={`${id}-view`}
          onChange={(event) =>
            props.onPickView(event.target.value === UNSAVED ? null : event.target.value)
          }
          value={props.activeView ?? UNSAVED}
        >
          <NativeSelectOption value={UNSAVED}>
            {props.activeView ? "Unsaved view" : "Current view"}
          </NativeSelectOption>
          {props.saved.map((view) => (
            <NativeSelectOption key={view.name} value={view.name}>
              {view.name}
            </NativeSelectOption>
          ))}
        </NativeSelect>
        <Popover onOpenChange={setNaming} open={naming}>
          <PopoverTrigger render={<Button type="button" variant="ghost" />}>
            <BookmarkSimpleIcon /> Save view
          </PopoverTrigger>
          <PopoverContent align="end" className="w-72 gap-3 p-3">
            <form
              className="flex flex-col gap-2"
              onSubmit={(event) => {
                event.preventDefault();
                const trimmed = name.trim();
                if (!trimmed) return;
                props.onSaveView(trimmed);
                setName("");
                setNaming(false);
              }}
            >
              <Label htmlFor={`${id}-name`}>Name this view</Label>
              <Input
                autoFocus
                id={`${id}-name`}
                maxLength={40}
                onChange={(event) => setName(event.target.value)}
                placeholder="e.g. Cash bookings this month"
                type="text"
                value={name}
              />
              <p className="text-xs text-muted-foreground">
                Saves what you have now: {props.saveSummary}. Kept in this browser.
              </p>
              <div className="flex justify-between gap-2">
                {props.activeView ? (
                  <Button
                    onClick={() => {
                      if (props.activeView) props.onDeleteView(props.activeView);
                      setNaming(false);
                    }}
                    size="sm"
                    type="button"
                    variant="ghost"
                  >
                    Delete “{props.activeView}”
                  </Button>
                ) : (
                  <span />
                )}
                <Button disabled={!name.trim()} size="sm" type="submit">
                  Save
                </Button>
              </div>
            </form>
          </PopoverContent>
        </Popover>
        <Popover>
          <PopoverTrigger render={<Button type="button" variant="ghost" />}>
            <ColumnsIcon /> Columns
            {hiddenColumns > 0 ? (
              <span className="text-muted-foreground tabular-nums">
                {props.columns.length - hiddenColumns}/{props.columns.length}
              </span>
            ) : null}
          </PopoverTrigger>
          <PopoverContent align="end" className="max-h-96 w-64 gap-1 overflow-y-auto p-2">
            <p className="px-2 py-1 text-xs text-muted-foreground">Show columns</p>
            {props.columns.map((column) => (
              // biome-ignore lint/a11y/noLabelWithoutControl: the Checkbox renders the control
              <label
                className="flex cursor-pointer items-center gap-2.5 rounded-lg px-2 py-1.5 text-sm hover:bg-muted"
                key={column.key}
              >
                <Checkbox
                  checked={!column.hidden}
                  onCheckedChange={() => props.onToggleColumn(column.key)}
                />
                {column.label}
              </label>
            ))}
          </PopoverContent>
        </Popover>
        <Button
          disabled={props.shownRows === 0}
          onClick={props.onExportAll}
          type="button"
          variant="outline"
        >
          <DownloadSimpleIcon /> Export {props.shownRows}
        </Button>
      </div>

      {props.chips.length > 0 ? (
        <div
          className="flex min-w-0 flex-wrap items-center gap-2"
          role="toolbar"
          aria-label="Active filters"
        >
          <span className="text-sm text-muted-foreground">Filters</span>
          {props.chips.map((chip) => (
            <span
              className="flex items-center rounded-full bg-primary/10 text-sm text-primary"
              key={chip.key}
            >
              <button
                className="rounded-l-full py-1 pr-1 pl-3 hover:underline"
                onClick={() => props.onEditChip(chip.key)}
                type="button"
              >
                {chip.label}
              </button>
              <button
                aria-label={`Remove filter ${chip.label}`}
                className="rounded-r-full py-1 pr-2 pl-1 hover:bg-primary/15"
                onClick={() => props.onRemoveChip(chip.key)}
                type="button"
              >
                <XIcon className="size-3.5" />
              </button>
            </span>
          ))}
          <Button
            className="h-auto p-0"
            onClick={props.onClearFilters}
            type="button"
            variant="link"
          >
            Clear all
          </Button>
          <span className="text-sm text-muted-foreground tabular-nums">
            · {hiddenRows} {hiddenRows === 1 ? "row" : "rows"} hidden
          </span>
        </div>
      ) : null}
    </div>
  );
}
